import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import BackToTop from '@/components/feature/BackToTop';
import JsonLd from '@/components/base/JsonLd';
import usePageMeta from '@/hooks/usePageMeta';
import { articles } from '@/mocks/news';

export default function ArticleDetail() {
  const { id } = useParams();
  const article = articles.find((item) => item.id === id);
  const related = articles.filter((item) => item.id !== id).slice(0, 3);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  usePageMeta({
    title: article ? `${article.title} - 看料大瓜` : '话题未找到 - 看料大瓜',
    description: article
      ? `${article.summary.slice(0, 80)}…`
      : '该话题可能已被移除，返回话题合集查看其他内容。',
    keywords: article ? `${article.tags.join(',')},看料大瓜,话题盘点` : '看料大瓜,话题合集',
    path: `/article/${id}`,
    ogType: 'article',
    image: article?.image,
  });

  if (!article) {
    return (
      <div id="top" className="min-h-screen bg-background-50">
        <SiteHeader />
        <div className="mx-auto flex max-w-3xl flex-col items-center px-4 py-40 text-center md:px-6">
          <i className="ri-emotion-sad-line text-4xl text-foreground-400" aria-hidden="true"></i>
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">没有找到这个话题</h1>
          <p className="mt-3 text-sm text-foreground-600">它可能已经被合并或移除，去话题合集看看其他内容吧。</p>
          <Link
            to="/topics"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回话题合集
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const structuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      image: article.image,
      datePublished: article.date,
      dateModified: article.date,
      inLanguage: 'zh-CN',
      author: { '@type': 'Organization', name: article.author },
      publisher: { '@type': 'Organization', name: '看料大瓜' },
      articleSection: article.categoryName,
      keywords: article.tags.join(','),
      mainEntityOfPage: `https://kanliaodagua.example/article/${article.id}`,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://kanliaodagua.example/' },
        { '@type': 'ListItem', position: 2, name: '话题合集', item: 'https://kanliaodagua.example/topics' },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: `https://kanliaodagua.example/article/${article.id}`,
        },
      ],
    },
  ];

  return (
    <div id="top" className="min-h-screen bg-background-50">
      <JsonLd data={structuredData} />
      <SiteHeader />
      <BackToTop />

      <div className="pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 pt-6 md:px-6">
          <Breadcrumb
            items={[
              { label: '话题合集', to: '/topics' },
              { label: article.categoryName, to: `/topics?cat=${article.category}` },
              { label: article.title },
            ]}
          />
        </div>
      </div>

      <article className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <header className="max-w-3xl">
          <div className="flex flex-wrap items-center gap-3 text-xs">
            <span className="rounded-full bg-accent-100 px-3 py-1 text-accent-900">{article.categoryName}</span>
            <span className="text-foreground-500">{article.date}</span>
            <span className="text-foreground-400">· {article.readTime}</span>
            <span className="flex items-center gap-1 text-foreground-500">
              <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
              热度 {article.heat}
            </span>
          </div>
          <h1 className="mt-5 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-4xl">
            {article.title}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">{article.summary}</p>
          <p className="mt-4 text-xs text-foreground-500">话题整理：{article.author}</p>
        </header>

        <div className="mt-8 h-64 w-full overflow-hidden rounded-lg md:h-[420px]">
          <img
            src={article.image}
            alt={article.title}
            title={`${article.title} - 看料大瓜`}
            className="h-full w-full object-cover object-top"
          />
        </div>

        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-8">
            <div className="rounded-lg border-l-4 border-primary-500 bg-background-100 p-5 md:p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-list-check-2 text-primary-600" aria-hidden="true"></i>
                要点速览
              </h2>
              <ul className="mt-3 space-y-2.5">
                {article.highlights.map((point) => (
                  <li key={point} className="flex items-start gap-2.5 text-sm leading-relaxed text-foreground-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-primary-500" aria-hidden="true"></i>
                    {point}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-8 space-y-5">
              {article.content.map((para, idx) => (
                <p key={idx} className="text-sm leading-loose text-foreground-800 md:text-[15px] md:leading-loose">
                  {para}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2 border-t border-background-200 pt-6">
              {article.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1.5 text-xs text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 rounded-lg bg-background-200 p-5 md:p-6">
              <h2 className="font-heading text-base font-bold text-foreground-950">理性吃瓜提示</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                本篇为话题梳理与现象观察，不包含对任何个人的评价。遇到存在争议的信息，请以官方渠道的说明为准，把判断权留给自己。
              </p>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h2 className="font-heading text-base font-bold text-foreground-950">相关话题</h2>
                <div className="mt-4 flex flex-col gap-5">
                  {related.map((item) => (
                    <Link key={item.id} to={`/article/${item.id}`} className="group flex gap-3">
                      <span className="h-16 w-20 shrink-0 overflow-hidden rounded-md">
                        <img
                          src={item.image}
                          alt={item.title}
                          title={`${item.title} - 看料大瓜`}
                          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-600">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">{item.categoryName}</span>
                      </span>
                    </Link>
                  ))}
                </div>
              </div>

              <Link
                to="/topics"
                className="mt-6 flex items-center justify-between rounded-lg bg-secondary-900 p-5 text-background-50 transition-colors hover:bg-secondary-800"
              >
                <span>
                  <span className="block font-heading text-base font-bold">浏览全部话题</span>
                  <span className="mt-1 block text-xs text-secondary-200">按分类补齐你的瓜田清单</span>
                </span>
                <i className="ri-arrow-right-line text-xl" aria-hidden="true"></i>
              </Link>
            </div>
          </aside>
        </div>

        <section className="mt-14 border-t border-background-200 pt-10">
          <h2 className="font-heading text-xl font-bold text-foreground-950">编辑推荐</h2>
          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((item) => (
              <ArticleCard key={`rec-${item.id}`} article={item} />
            ))}
          </div>
        </section>
      </article>

      <SiteFooter />
    </div>
  );
}